/**
 * The Board's live editor: Tiptap bound to the space's shared Yjs document.
 * Everyone on the page (and the AI, which edits through the server) types
 * into one document; carets carry each person's name. Undo belongs to the
 * collaboration layer, so it undoes only your own edits.
 *
 * "/" at the start of a word opens the insert menu (`boardCommands`).
 *
 * @module components/spaces/board/BoardEditor
 */
import { spaceBoardExtensions } from "@amoreg/shared/spaceBoard";
import type { Editor } from "@tiptap/core";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import { Placeholder } from "@tiptap/extensions";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Awareness } from "y-protocols/awareness";
import type * as Y from "yjs";

import { cn } from "../../../lib/utils";
import { BoardCodeBlock } from "./BoardCodeBlock";
import {
  STARTER_CHART,
  filterBoardCommands,
  slashQuery,
  type BoardCommand,
  type BoardCommandId,
} from "./boardCommands";

import "./board.css";

interface SlashState {
  readonly query: string;
  /** Document range of "/query", replaced by the chosen block. */
  readonly from: number;
  readonly to: number;
  readonly left: number;
  readonly top: number;
}

function runBoardCommand(editor: Editor, id: BoardCommandId, range: { from: number; to: number }) {
  const chain = editor.chain().focus().deleteRange(range);
  switch (id) {
    case "heading1":
      return chain.setNode("heading", { level: 1 }).run();
    case "heading2":
      return chain.setNode("heading", { level: 2 }).run();
    case "heading3":
      return chain.setNode("heading", { level: 3 }).run();
    case "bullets":
      return chain.toggleBulletList().run();
    case "numbers":
      return chain.toggleOrderedList().run();
    case "tasks":
      return chain.toggleTaskList().run();
    case "table":
      return chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    case "quote":
      return chain.toggleBlockquote().run();
    case "divider":
      return chain.setHorizontalRule().run();
    case "code":
      return chain.setCodeBlock().run();
    case "chart":
      return chain
        .insertContent({
          type: "codeBlock",
          attrs: { language: "chart" },
          content: [{ type: "text", text: STARTER_CHART }],
        })
        .run();
  }
}

/** The "/query" before the caret, with where to draw the menu. */
function readSlashState(editor: Editor, host: HTMLElement | null): SlashState | null {
  const { state, view } = editor;
  const { $from, empty } = state.selection;
  if (!empty || !$from.parent.isTextblock || $from.parent.type.name === "codeBlock") return null;
  const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, "￼");
  const query = slashQuery(textBefore);
  if (query === null || host === null) return null;
  const from = $from.pos - query.length - 1;
  const caret = view.coordsAtPos($from.pos);
  const box = host.getBoundingClientRect();
  return { query, from, to: $from.pos, left: caret.left - box.left, top: caret.bottom - box.top + 6 };
}

export function BoardEditor({
  doc,
  awareness,
  editable,
  user,
  placeholder,
}: {
  readonly doc: Y.Doc;
  readonly awareness: Awareness;
  readonly editable: boolean;
  /** Provisional caret label; the server stamps the real one for everyone else. */
  readonly user: { readonly name: string; readonly color: string };
  /** What an empty page says; the Board's own wording by default. */
  readonly placeholder?: string | undefined;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [slash, setSlash] = useState<SlashState | null>(null);
  const [active, setActive] = useState(0);
  const commands = useMemo(() => (slash ? filterBoardCommands(slash.query) : []), [slash]);
  // The key handler is created once with the editor; it reads the menu
  // through refs so it always sees the current one.
  const menuRef = useRef({ slash, commands, active });
  menuRef.current = { slash, commands, active };

  const editor = useEditor(
    {
      immediatelyRender: false,
      editable,
      extensions: [
        ...spaceBoardExtensions({ codeBlock: BoardCodeBlock }),
        Collaboration.configure({ document: doc }),
        CollaborationCaret.configure({ provider: { awareness }, user }),
        Placeholder.configure({
          placeholder: ({ editor: current }) =>
            current.isEmpty
              ? (placeholder ??
                "Write the space's status, deliveries and notes. Type / for a heading, checklist, table or chart.")
              : "Type / to insert",
        }),
      ],
      editorProps: {
        attributes: { class: "space-board-prose", "aria-label": "Board" },
        handleKeyDown: (_view, event) => {
          const menu = menuRef.current;
          if (menu.slash === null || menu.commands.length === 0) return false;
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            const step = event.key === "ArrowDown" ? 1 : -1;
            setActive((index) => (index + step + menu.commands.length) % menu.commands.length);
            return true;
          }
          if (event.key === "Enter" || event.key === "Tab") {
            choose(menu.commands[menu.active] ?? menu.commands[0]!);
            return true;
          }
          if (event.key === "Escape") {
            setSlash(null);
            return true;
          }
          return false;
        },
      },
      onUpdate: ({ editor: current }) => setSlash(readSlashState(current, hostRef.current)),
      onSelectionUpdate: ({ editor: current }) =>
        setSlash(readSlashState(current, hostRef.current)),
      onBlur: () => setSlash(null),
    },
    [doc, awareness],
  );

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  useEffect(() => setActive(0), [slash?.query]);

  function choose(command: BoardCommand) {
    const current = menuRef.current.slash;
    if (!editor || current === null) return;
    runBoardCommand(editor, command.id, { from: current.from, to: current.to });
    setSlash(null);
  }

  return (
    <div ref={hostRef} className="relative" data-space-board-editor="">
      <EditorContent editor={editor} />
      {slash !== null && commands.length > 0 ? (
        <div
          role="listbox"
          aria-label="Insert"
          className="absolute z-30 w-64 overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg"
          style={{ left: slash.left, top: slash.top }}
          data-space-board-slash=""
        >
          {commands.map((command, index) => (
            <button
              key={command.id}
              type="button"
              role="option"
              aria-selected={index === active}
              // Keep focus in the editor so the range stays valid.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(command);
              }}
              onMouseEnter={() => setActive(index)}
              className={cn(
                "flex w-full cursor-pointer flex-col items-start rounded-lg px-2.5 py-1.5 text-left",
                index === active ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
              )}
            >
              <span className="text-[13px] font-medium">{command.label}</span>
              <span className="text-xs text-muted-foreground">{command.hint}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
